import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default function DeviceMapData({ data, deviceName }) {

    const calculateStatus = useCallback((equipment) => {
        if (!equipment || !equipment.lastSeen) return "Not Available";

        const now = new Date();
        const lastSeen = new Date(equipment.lastSeen);
        const diffHours = (now - lastSeen) / (1000 * 60 * 60);

        if (diffHours > 12) {
            return "Not Available";
        }

        if (equipment.ignition && equipment.speed > 0) {
            return "Running";
        } else if (equipment.ignition && equipment.speed === 0) {
            return "Idle";
        } else {
            return "Stopped";
        }
    }, []);

    const equipmentData = useMemo(() => {
        if (!data || !Array.isArray(data)) return [];

        const coordinates = {};
        data.forEach(device => {
            const key = `${device.latitude},${device.longitude}`;
            if (coordinates[key]) {
                coordinates[key].count++;
                coordinates[key].devices.push(device.imei || device.id);
            } else {
                coordinates[key] = { count: 1, devices: [device.imei || device.id] };
            }
        });

        Object.entries(coordinates).forEach(([coord, info]) => {
            if (info.count > 1) {
            }
        });

        return data.map(device => {
            let lat = device.latitude;
            let lng = device.longitude;

            if (!lat || !lng || lat === 0 || lng === 0) {
                lat = 20.5937 + (Math.random() * 15 - 7.5);
                lng = 78.9629 + (Math.random() * 15 - 7.5);
            }

            return {
                id: device.imei || device.id,
                name:  deviceName ? deviceName :`Device ${device.imei?.substring(8) || device.id}`,
                lat: lat,
                lng: lng,
                speed: device.speed || 0,
                ignition: device.ignition === 1,
                externalBattery: device.externalVoltage || 0,
                internalBattery: device.batteryVoltage || 0,
                lastSeen: device.serverTime || new Date().toISOString(),
                direction: device.direction || 0,
                engineStatus: device.engineStatus || false
            };
        });
    }, [data]);

    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markersRef = useRef({});
    const [selectedEquipment, setSelectedEquipment] = useState(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("All");

    const getStatusColor = useCallback((status) => {
        switch (status) {
            case "Running": return "#45BEA9";
            case "Idle": return "#FF9800";
            case "Stopped": return "#F44336";
            case "Not Available": return "#9E9E9E";
            default: return "#2196F3";
        }
    }, []);

    const getBatteryColor = useCallback((percentage) => {
        if (percentage >= 70) return "#45BEA9";
        if (percentage >= 40) return "#45BEA9";
        return "#45BEA9";
    }, []);

    const createCustomIcon = useCallback((equipment) => {
        if (!equipment || !equipment.id) {
            return L.divIcon({});
        }
        const displayId = equipment.id.length > 4 ? equipment.id.substring(equipment.id.length - 4) : equipment.id;
        const status = calculateStatus(equipment);
        const color = getStatusColor(status);

        return L.divIcon({
            className: 'custom-marker',
            html: `
                <div style="
                    width: 40px;
                    height: 40px;
                    background-color: ${color};
                    border-radius: 50%;
                    border: 2px solid white;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: bold;
                    cursor: pointer;
                    transition: transform 0.2s;
                    font-size: 10px;
                    position: relative;
                ">
                    ${displayId}
                    <div style="
                        position: absolute;
                        bottom: -2px;
                        right: -2px;
                        width: 12px;
                        height: 12px;
                        background-color: ${equipment.ignition ? '#4CAF50' : '#F44336'};
                        border-radius: 50%;
                        border: 1px solid white;
                    "></div>
                </div>
            `,
            iconSize: [40, 40],
            iconAnchor: [20, 40],
            popupAnchor: [0, -40]
        });
    }, [calculateStatus, getStatusColor]);

    const centerMapOnEquipment = useCallback((equipment) => {
        if (mapInstance.current && equipment) {
            mapInstance.current.setView([equipment.lat, equipment.lng], 15);
            if (markersRef.current[equipment.id]) {
                markersRef.current[equipment.id].openPopup();
            }
        }
    }, []);

    useEffect(() => {
        if (!mapRef.current) return;
        if (mapInstance.current) {
            mapInstance.current.remove();
            mapInstance.current = null;
            markersRef.current = {};
            setMapLoaded(false);
        }
        try {
            const centerLat = equipmentData[0]?.lat || 20.5937;
            const centerLng = equipmentData[0]?.lng || 78.9629;

            mapInstance.current = L.map(mapRef.current).setView([centerLat, centerLng], 5);

            // Add tile layer
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                maxZoom: 19
            }).addTo(mapInstance.current);

            // Add markers for each equipment
            equipmentData.forEach(equipment => {
                if (!equipment.lat || !equipment.lng || isNaN(equipment.lat) || isNaN(equipment.lng)) {
                    return;
                }

                const marker = L.marker([equipment.lat, equipment.lng], {
                    icon: createCustomIcon(equipment)
                }).addTo(mapInstance.current);

                // Create popup content
                const popupContent = `
                    <div style="padding: 15px; min-width: 280px; max-width: 350px; font-family: Arial, sans-serif;">
                        <div style="display: flex; align-items: center; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 1px solid #eee;">
                            <div>
                                <h5 style="margin: 0 0 5px 0; color: #333;">${equipment.name}</h5>
                                <div style="font-size: 12px; color: #666;">IMEI: ${equipment.id}</div>
                            </div>
                        </div>

                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px;">
                            <div>
                                <div style="font-size: 11px; color: #291f1fff; margin-bottom: 3px;">Ignition</div>
                                <div style="background-color: ${equipment.ignition ? '#45BEA9' : '#45BEA9'}; 
                                    color: ${equipment.ignition ? '#fff' : '#fff'}; 
                                    padding: 6px 10px; border-radius: 4px; font-weight: bold; text-align: center; font-size: 12px;">
                                    ${equipment.ignition ? 'ON' : 'OFF'}
                                </div>
                            </div>

                            <div>
                                <div style="font-size: 11px; color: #291f1fff margin-bottom: 3px;">Speed</div>
                                <div style="background-color: #45BEA9; color: #fff; padding: 6px 10px; border-radius: 4px; font-weight: bold; text-align: center; font-size: 12px;">
                                    ${equipment.speed} km/h
                                </div>
                            </div>
                            
                            <div>
                                <div style="font-size: 11px; color: #291f1fff; margin-bottom: 3px;">Direction</div>
                                <div style="background-color: #45BEA9; color: #fff; padding: 6px 10px; border-radius: 4px; font-weight: bold; text-align: center; font-size: 12px;">
                                    ${equipment.direction}°
                                </div>
                            </div>
                            
                            <div>
                                <div style="font-size: 11px; color: #666; margin-bottom: 3px;">Engine</div>
                                <div style="background-color: ${equipment.engineStatus ? '#45BEA9' : '#45BEA9'}; 
                                    color: ${equipment.engineStatus ? '#fff' : '#fff'}; 
                                    padding: 6px 10px; border-radius: 4px; font-weight: bold; text-align: center; font-size: 12px;">
                                    ${equipment.engineStatus ? 'ON' : 'OFF'}
                                </div>
                            </div>
                        </div>

                        <div style="margin-bottom: 15px;">
                            <div style="font-size: 13px; color: #333; margin-bottom: 8px; font-weight: bold;">
                                Battery Status
                            </div>
                            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
                                <div>
                                    <div style="font-size: 11px; color: #291f1fff; margin-bottom: 3px;">External</div>
                                    <div style="background-color: ${getBatteryColor(equipment.externalBattery)}20; 
                                        color: ${getBatteryColor(equipment.externalBattery)}; 
                                        padding: 6px 10px; border-radius: 4px; font-weight: bold; text-align: center; position: relative; font-size: 12px;">
                                        ${equipment.externalBattery}V
                                        <div style="position: absolute; bottom: 2px; left: 10%; right: 10%; height: 3px; 
                                            background-color: ${getBatteryColor(equipment.externalBattery)}; border-radius: 2px;">
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div style="font-size: 11px; color: #291f1fff margin-bottom: 3px;">Internal</div>
                                    <div style="background-color: ${getBatteryColor(equipment.internalBattery)}20; 
                                        color: ${getBatteryColor(equipment.internalBattery)}; 
                                        padding: 6px 10px; border-radius: 4px; font-weight: bold; text-align: center; position: relative; font-size: 12px;">
                                        ${equipment.internalBattery}V
                                        <div style="position: absolute; bottom: 2px; left: 10%; right: 10%; height: 3px; 
                                            background-color: ${getBatteryColor(equipment.internalBattery)}; border-radius: 2px;">
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style="background-color: #f9f9f9; padding: 10px; border-radius: 4px; font-size: 12px; color: #666;">
                            <div style="margin-bottom: 5px;">
                                <strong>Last Seen:</strong> 
                                
                                ${moment(equipment.lastSeen).format('DD-MMM-YYYY HH:mm:ss')}
                              
                            </div>
                            <div>
                                <strong>Location:</strong> ${equipment.lat.toFixed(6)}, ${equipment.lng.toFixed(6)}
                            </div>
                            
                        </div>
                    </div>
                `;

                marker.bindPopup(popupContent);

                // Add click event to select equipment
                marker.on('click', () => {
                    setSelectedEquipment(equipment);
                });

                markersRef.current[equipment.id] = marker;
            });

            // Fit bounds to show all markers
            if (equipmentData.length > 0) {
                const validMarkers = equipmentData.filter(eq =>
                    eq.lat && eq.lng && !isNaN(eq.lat) && !isNaN(eq.lng)
                );

                if (validMarkers.length > 0) {
                    const bounds = L.latLngBounds(validMarkers.map(eq => [eq.lat, eq.lng]));
                    mapInstance.current.fitBounds(bounds, {
                        padding: [50, 50],
                        maxZoom: 15
                    });
                }
            }

            setMapLoaded(true);

        } catch (error) {
            setMapLoaded(false);
        }

        return () => {
            if (mapInstance.current) {
                mapInstance.current.remove();
                mapInstance.current = null;
            }
            markersRef.current = {};
        };
    }, [equipmentData, createCustomIcon, getBatteryColor]);

    useEffect(() => {
        if (selectedEquipment && mapInstance.current) {
            centerMapOnEquipment(selectedEquipment);
        }
    }, [selectedEquipment, centerMapOnEquipment]);

    return (
        <>
           
        <div style={{
            width: "100%",
            height: "100vh",
            display: "flex",
            position: "relative"
        }}
            className="row m-0 mt-4">
            <div style={{
                height: "100%",
                position: "relative"
            }}
                className="col-sm-12 p-0">
                <div
                    ref={mapRef}
                    style={{ width: "100%", height: "100%" }}
                />

                {!mapLoaded && equipmentData.length > 0 && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: '#f5f5f5',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            fontSize: '18px',
                            marginBottom: '15px',
                            color: '#333'
                        }}>
                            Loading Equipment Map ({equipmentData.length} items)...
                        </div>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #3498db',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                        <style>{`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}</style>
                    </div>
                )}
            </div>
        </div>
        </>
    );
}